import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import styled, { useTheme } from "styled-components";
import { useHistory } from "react-router-dom";
import { AuthoritiesEnum, IntegrationTypeEnum, RoutesEnum } from "~/types/enums";
import mixpanel from "mixpanel-browser";
import { Avatar, Button, Card, Dropdown, Icon, Picture, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import integrationSelectors from "~/store/integrations/selectors";
import { getSlackSpaceUrl } from "~/lib/helpers";
import lists from "~/lib/lists";
import authSelectors from "~/store/auth/selectors";
import useAbacExt from "~/hooks/abac";
import membersActionCreators from "~/store/members/actions";
import { MemberActionEnum } from "~/store/members/types";
const StyledAvatar = styled(Avatar)`
  z-index: 2;
`;
const StyledDropdown = styled(Dropdown)`
  position: absolute;
  right: 0.5em;
  top: 0.5em;
`;
const StyledButton = styled(Button)`
  position: absolute;
  left: 0.5em;
  top: 0.5em;
`;
const Content = styled.div`
  flex-grow: 1;
  width: 90%;
`;
const StyledText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MemberCard = _ref => {
  let {
    children,
    ctaDisabled = false,
    noActions = false,
    member,
    onClick,
    ...rest
  } = _ref;
  const theme = useTheme();
  const history = useHistory();
  const {
    t
  } = useTranslation();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const user = useSelector(authSelectors.getUser);
  const hasSlack = slackIntegration && member.metadata.slackUserName && member.metadata.slackUserId;
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  const handleView = () => {
    if (typeof onClick === "function") {
      onClick(member);
    } else {
      history.push(user.id === member.id ? RoutesEnum.PROFILE : RoutesEnum.MEMBER.replace(":memberId", member.id));
    }
    mixpanel.track(`Member Card – View "${member.firstName} ${member.lastName}" profile`, {
      memberId: member.id
    });
  };
  const handleSlack = e => {
    e.stopPropagation();
    if (slackIntegration) {
      window.open(`${getSlackSpaceUrl(slackIntegration)}/team/${member.metadata.slackUserId}`, "_blank");
    }
    mixpanel.track(`Member Card – View "${member.firstName} ${member.lastName}" slack`, {
      memberId: member.id,
      slackId: member.metadata.slackUserId
    });
  };
  const options = [];
  if (member.isTemporary && member.email && !member.isBot && userHas(AuthoritiesEnum.INVITATIONS_WRITE)) {
    options.push({
      id: 1,
      name: t("MemberCard.inviteToJoyn"),
      icon: "user-plus",
      action: e => {
        e.stopPropagation();
        dispatch(membersActionCreators.actionChanged(MemberActionEnum.INVITE, member.id));
      }
    });
  }
  if (userHas([AuthoritiesEnum.EXPERIMENTAL, AuthoritiesEnum.MEMBERS_WRITE]) && member.isTemporary && !member.isBot) {
    options.push({
      id: 2,
      name: t("MemberCard.mergeMemberTo"),
      icon: "stackGroup",
      action: e => {
        e.stopPropagation();
        dispatch(membersActionCreators.actionChanged(MemberActionEnum.MERGE, member.id));
      }
    });
  }
  return /*#__PURE__*/React.createElement(Card, _extends({
    shadow: true,
    align: "center",
    onClick: noActions ? undefined : handleView,
    minHeight: "23rem",
    color: "background"
  }, rest), hasSlack && /*#__PURE__*/React.createElement(StyledButton, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("MemberCard.writeInSlack"),
    display: "block",
    color: "transparent",
    fontSize: 4,
    s: {
      p: 2
    },
    onClick: handleSlack,
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 4.5,
    src: "message",
    display: "inline-block"
  })), !noActions && !!options.length && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, options.map(option => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        onItemClick();
        if (option.action) option.action(e);
      },
      key: option.id
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: option.icon,
      s: {
        mr: 2
      },
      color: "primary"
    }), option.name)));
  }), /*#__PURE__*/React.createElement(StyledAvatar, {
    user: member,
    size: 25,
    color: member.isTemporary ? theme.colors.borders.secondary : theme.colors.primary.main,
    s: {
      mb: 4,
      mt: 6
    },
    showStatus: true,
    showEmoji: true
  }), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(StyledText, {
    element: "h3",
    lineHeight: 6
  }, member.firstName, " ", member.lastName), member.position && /*#__PURE__*/React.createElement(StyledText, {
    element: "span",
    color: "textSecondary",
    display: "block",
    fontSize: 3.5,
    lineHeight: 4.5,
    s: {
      mt: 1
    }
  }, member.position)), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    fontSize: 3
  }, /*#__PURE__*/React.createElement(Picture, {
    border: "squared",
    src: lists.members.types.logos(theme)[member.type],
    width: "1.5rem",
    height: "1.5rem",
    backgroundSize: "contain",
    display: "inline-block"
  })));
};
export default MemberCard;